import Link from '@/update-monitor/Link'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ReactElement } from 'react'

import NextHistory from '@/NextHistory'
import { Loading } from '@/components/Loading'
import { SignUpLayout } from '@/components/layout/web'
import { Button } from '@/components/ui/button'
import { WebPageBackButton } from '@/components/web-page-back-button'
import { ContractType } from '@/gql/generated/graphql'
import { useClientOnly } from '@/hooks/useClientOnly'
import { useNoSuspenseQuery } from '@/hooks/useQuery'
import { LatestContractsQuery } from '@/queries'

export default function Agreement() {
  useClientOnly()
  const router = useRouter()
  const registrationType = router.query.registration_type as string
  const code = router.query.code as string

  const [{ data: latestContractsData, fetching }] = useNoSuspenseQuery({
    query: LatestContractsQuery,
    requestPolicy: 'cache-and-network',
  })

  if (fetching) {
    return (
      <div className="grid h-full flex-grow place-items-center">
        <Loading />
      </div>
    )
  }

  const notSignedContracts = latestContractsData?.latestContracts
    ?.sort((a) => (a.contractType === ContractType.TermOfUsage ? -1 : 1))
    .filter((contract) => !contract.signed)

  return (
    <>
      <Head>
        <title>ロジックラボ | 利用規約・プライバシーポリシーの同意</title>
      </Head>

      <WebPageBackButton
        onClick={() => NextHistory.backOrPush('https://www.sonyged.com/logiq')}
      />

      <div className="grow px-5 pt-[54px] md:pt-[70px] lg:pt-[80px]">
        <div className="mx-auto w-full rounded-lg bg-white px-4 py-8 md:max-w-[728px] md:pb-[60px] md:pt-[50px] lg:max-w-[940px]">
          <h3 className="mb-[17px] text-center text-[18px] font-bold md:mb-[30px] md:text-2xl lg:text-4xl">
            利用規約・プライバシーポリシー
          </h3>

          <div
            tabIndex={0}
            className="mx-auto flex h-[275px] w-full flex-col gap-8 overflow-y-auto rounded-md border border-outline px-4 py-10 md:h-[306px] md:max-w-[640px]"
          >
            {notSignedContracts?.map((contract) => (
              <div key={contract.id}>
                <h2 className="mb-4 text-center text-xl font-bold text-[#666666]">
                  {contract.title}
                </h2>

                <div className="policy-section">
                  <div
                    className="text-[15px] leading-normal text-[#666666]"
                    dangerouslySetInnerHTML={{
                      __html: contract.content,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="mt-5 text-center text-xs font-medium md:mt-[30px]">
            利用規約およびプライバシーポリシーを確認し、同意の上次へ進んでださい。
          </p>

          <Button
            variant="light"
            asChild={true}
            className="mx-auto mt-5 flex w-[270px] md:mt-[50px]"
            disabled={!notSignedContracts || notSignedContracts.length === 0}
          >
            <Link
              href={{
                pathname: '/web/sign_up',
                query: {
                  agreement: 'true',
                  registration_type: registrationType,
                  code,
                },
              }}
              as="/web/sign_up"
            >
              同意する
            </Link>
          </Button>
        </div>
      </div>
    </>
  )
}

Agreement.getLayout = (page: ReactElement) => (
  <SignUpLayout title="新規アカウント登録">{page}</SignUpLayout>
)
